import type { MockContext } from '@mocks/mockFetch';

type MockHandler = (ctx: MockContext) => Promise<Response | null> | Response | null;
type MetricSample = { value: number; observedAt: string; labels: Record<string, string>; flags?: Array<{ name: string; labels: Record<string, string> }> };
type ProbeFixture = {
  observedAt: string;
  loss: number;
  packetsSent: number;
  packetsReceived: number;
  executionFailed?: boolean;
  min?: number;
  avg?: number;
  max?: number;
  deviation?: number;
};
type ProbeProfile = { averageMs: number; variationMs: number; phase: number; lossAt: (index: number) => number };
type CandidateFixture = { sourceDeviceId: string; sourceIp: string; destinationIp: string; history: ProbeFixture[]; stale?: boolean; ageSeconds?: number };
type CellFixture = { sourceSiteId: string; destinationSiteId: string; candidates: CandidateFixture[] };

// Keep the mock visually live whenever the dashboard is opened. The paths
// below intentionally cover every dashboard state while omitted pairs are the
// matrix's no-data examples.
const LIVE_CAPTURED_AT = new Date().toISOString();
const PROBE_INTERVAL_MS = 10 * 60 * 1000;
const HISTORY_DURATION_MS = 24 * 60 * 60 * 1000;
const PROBE_COUNT = HISTORY_DURATION_MS / PROBE_INTERVAL_MS + 1;
const RTT_METRIC = 'ping_mesh_rtt_seconds';
const LOSS_METRIC = 'ping_mesh_packet_loss_ratio';
const DISPLAY_HISTOGRAM_BUCKETS = [
  { label: '≤ 1 ms', lowerMs: 0, upperMs: 1 },
  { label: '1–10 ms', lowerMs: 1, upperMs: 10 },
  { label: '10–50 ms', lowerMs: 10, upperMs: 50 },
  { label: '50–100 ms', lowerMs: 50, upperMs: 100 },
  { label: '100–300 ms', lowerMs: 100, upperMs: 300 },
  { label: '> 300 ms', lowerMs: 300, upperMs: null }
] as const;

function probeTime(index: number): string {
  const live = new Date(LIVE_CAPTURED_AT).getTime();
  if (index === PROBE_COUNT - 1) return LIVE_CAPTURED_AT;
  return new Date(live - (PROBE_COUNT - 1 - index) * PROBE_INTERVAL_MS + ((index * 37) % 43) * 1000).toISOString();
}

function fullProbeHistory(profile: ProbeProfile): ProbeFixture[] {
  return Array.from({ length: PROBE_COUNT }, (_, index) => {
    const loss = profile.lossAt(index);
    const packetsSent = 5;
    const packetsReceived = Math.round(packetsSent * (1 - loss));
    if (loss === 1) return { observedAt: probeTime(index), loss, packetsSent, packetsReceived };
    // A small number of retained probes have bounded, believable excursions.
    // They create a useful histogram tail without letting rare samples flatten
    // the selected-window chart into an uninformative seconds-scale view.
    const majorExcursion = Math.min(profile.averageMs * .55, 72);
    const minorExcursion = Math.min(profile.averageMs * .2, 20);
    const transientSpike = index % 89 === 37 ? majorExcursion : index % 47 === 19 ? minorExcursion : 0;
    const average = Math.max(1, profile.averageMs + Math.sin(index * 0.29 + profile.phase) * profile.variationMs + transientSpike + (loss > 0 ? loss * (profile.variationMs * 4 + 7) : 0));
    const spread = Math.max(0.4, profile.variationMs * (0.46 + ((index * 11) % 5) / 10));
    return { observedAt: probeTime(index), loss, packetsSent, packetsReceived, min: (average - spread * 1.3) / 1000, avg: average / 1000, max: (average + spread * 2.1) / 1000, deviation: spread / 1000 };
  });
}

const CELLS: CellFixture[] = [
  { sourceSiteId: 'site-campus-b-firewall-demo', destinationSiteId: 'site-nyc', candidates: [
    { sourceDeviceId: 'pan-campus-b', sourceIp: '10.110.120.2', destinationIp: '10.60.70.8', history: fullProbeHistory({ averageMs: 29, variationMs: 2.7, phase: 0.4, lossAt: (index) => index % 43 === 18 ? 0.2 : index % 71 === 34 ? 0.4 : 0 }) },
    { sourceDeviceId: 'pan-campus-b-ha', sourceIp: '10.110.120.3', destinationIp: '10.60.70.8', history: fullProbeHistory({ averageMs: 31, variationMs: 2.1, phase: 0.8, lossAt: () => 0 }) }
  ] },
  { sourceSiteId: 'site-campus-b-firewall-demo', destinationSiteId: 'site-bangalore-demo', candidates: [{ sourceDeviceId: 'pan-campus-b', sourceIp: '10.110.120.2', destinationIp: '10.110.130.2', history: fullProbeHistory({ averageMs: 0, variationMs: 0, phase: 0, lossAt: () => 1 }) }] },
  { sourceSiteId: 'site-campus-b-firewall-demo', destinationSiteId: 'site-ams', candidates: [{ sourceDeviceId: 'pan-campus-b', sourceIp: '10.110.120.2', destinationIp: '10.40.50.8', history: fullProbeHistory({ averageMs: 112, variationMs: 7.5, phase: 0.3, lossAt: () => 0 }) }] },
  { sourceSiteId: 'site-bangalore-demo', destinationSiteId: 'site-nyc', candidates: [{ sourceDeviceId: 'juniper-bangalore', sourceIp: '10.110.130.2', destinationIp: '10.60.70.8', history: fullProbeHistory({ averageMs: 35, variationMs: 4.2, phase: 1.2, lossAt: (index) => index === PROBE_COUNT - 1 ? 0.5 : index % 29 === 7 ? 0.2 : index % 67 === 22 ? 0.4 : 0 }) }] },
  { sourceSiteId: 'site-bangalore-demo', destinationSiteId: 'site-campus-b-firewall-demo', candidates: [{ sourceDeviceId: 'juniper-bangalore', sourceIp: '10.110.130.2', destinationIp: '10.110.120.2', stale: true, ageSeconds: 780, history: fullProbeHistory({ averageMs: 31, variationMs: 2.2, phase: 2.3, lossAt: (index) => index === PROBE_COUNT - 1 ? 0.2 : index % 59 === 14 ? 0.2 : 0 }) }] },
  { sourceSiteId: 'site-bangalore-demo', destinationSiteId: 'site-sfo', candidates: [{ sourceDeviceId: 'juniper-bangalore', sourceIp: '10.110.130.2', destinationIp: '10.20.30.8', history: fullProbeHistory({ averageMs: 148, variationMs: 8.8, phase: 1.8, lossAt: () => 0 }) }] },
  { sourceSiteId: 'site-sfo', destinationSiteId: 'site-nyc', candidates: [{ sourceDeviceId: 'edge-sfo-01', sourceIp: '10.20.30.8', destinationIp: '10.60.70.8', history: fullProbeHistory({ averageMs: 67, variationMs: 3.4, phase: 2.5, lossAt: () => 0 }) }] },
  { sourceSiteId: 'site-sfo', destinationSiteId: 'site-ams', candidates: [{ sourceDeviceId: 'edge-sfo-01', sourceIp: '10.20.30.8', destinationIp: '10.40.50.8', history: fullProbeHistory({ averageMs: 139, variationMs: 9.4, phase: 1.1, lossAt: (index) => index === PROBE_COUNT - 1 ? 0.2 : index % 51 === 10 ? 0.2 : 0 }) }] },
  { sourceSiteId: 'site-nyc', destinationSiteId: 'site-sfo', candidates: [{ sourceDeviceId: 'nyc-core-01', sourceIp: '10.60.70.8', destinationIp: '10.20.30.8', history: fullProbeHistory({ averageMs: 64, variationMs: 3.1, phase: 0.6, lossAt: () => 0 }) }] },
  { sourceSiteId: 'site-nyc', destinationSiteId: 'site-bangalore-demo', candidates: [{ sourceDeviceId: 'nyc-core-01', sourceIp: '10.60.70.8', destinationIp: '10.110.130.2', stale: true, ageSeconds: 1380, history: fullProbeHistory({ averageMs: 0, variationMs: 0, phase: 0, lossAt: () => 1 }) }] },
  { sourceSiteId: 'site-ams', destinationSiteId: 'site-nyc', candidates: [{ sourceDeviceId: 'ams-edge-01', sourceIp: '10.40.50.8', destinationIp: '10.60.70.8', stale: true, ageSeconds: 940, history: fullProbeHistory({ averageMs: 86, variationMs: 5.1, phase: 2.9, lossAt: (index) => index === PROBE_COUNT - 1 ? 0.5 : index % 38 === 16 ? 0.4 : 0 }) }] },
  { sourceSiteId: 'site-ams', destinationSiteId: 'site-sfo', candidates: [{ sourceDeviceId: 'ams-edge-01', sourceIp: '10.40.50.8', destinationIp: '10.20.30.8', history: fullProbeHistory({ averageMs: 141, variationMs: 8.4, phase: 1.6, lossAt: (index) => index === PROBE_COUNT - 1 ? 0.2 : 0 }) }] }
];

function duration(value: string): number | undefined {
  const match = /^(\d+)(m|h)$/.exec(value.trim());
  if (!match) return undefined;
  return Number(match[1]) * (match[2] === 'h' ? 60 * 60 * 1000 : 60 * 1000);
}

function historyForRange(history: ProbeFixture[], lookback: number, generatedAt: Date): ProbeFixture[] {
  const count = Math.min(history.length, Math.floor(lookback / PROBE_INTERVAL_MS) + 1);
  return history.slice(-count).map((probe, index) => {
    const offset = (count - 1 - index) * PROBE_INTERVAL_MS;
    const completedAt = index === count - 1
      ? generatedAt
      : new Date(generatedAt.getTime() - offset + ((index * 37) % 43) * 1000);
    return { ...probe, observedAt: completedAt.toISOString() };
  });
}

function samples(history: ProbeFixture[], value: (probe: ProbeFixture) => number | undefined, labels: Record<string, string> = {}, flagsFor?: (probe: ProbeFixture) => string[]): MetricSample[] {
  return history.flatMap((probe) => {
    const result = value(probe);
    if (result === undefined) return [];
    const flags = flagsFor?.(probe) ?? [];
    return [{ value: result, observedAt: probe.observedAt, labels, ...(flags.length > 0 ? { flags: flags.map((name) => ({ name, labels: {} })) } : {}) }];
  });
}

function percentile(values: number[], quantile: number): number {
  const sorted = values.slice().sort((left, right) => left - right);
  if (sorted.length === 0) return 0;
  const position = quantile * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function packetRTTs(history: ProbeFixture[]): number[] {
  return history.flatMap((probe) => {
    if (probe.min === undefined || probe.max === undefined || probe.packetsReceived <= 0) return [];
    return Array.from({ length: probe.packetsReceived }, (_, index) => {
      const position = probe.packetsReceived === 1 ? 0.5 : index / (probe.packetsReceived - 1);
      return probe.min! + (probe.max! - probe.min!) * position;
    });
  });
}

function rttSeries(history: ProbeFixture[], histogramRTTs: number[]) {
  const p95 = histogramRTTs.length > 0 ? percentile(histogramRTTs, 0.95) : undefined;
  return [
    // Per-probe packet loss is a plain gauge series. The BFF only annotates
    // RTT max samples, because those are evaluated against histogram P95.
    { name: LOSS_METRIC, samples: samples(history, (probe) => probe.loss) },
    { name: RTT_METRIC, samples: [
      ...samples(history, (probe) => probe.min, { stat: 'min' }),
      ...samples(history, (probe) => probe.avg, { stat: 'avg' }),
      ...samples(history, (probe) => probe.max, { stat: 'max' }, (probe) => probe.max === undefined ? [] : [
        ...(p95 !== undefined && probe.max > p95 ? ['above_p95'] : [])
      ]),
      ...samples(history, (probe) => probe.deviation, { stat: 'mdev' })
    ].sort((left, right) => new Date(right.observedAt).getTime() - new Date(left.observedAt).getTime()) }
  ];
}

function latestCandidate(candidate: CandidateFixture, generatedAt: Date) {
  const probe = candidate.history[candidate.history.length - 1];
  const status = probe.loss === 1 ? 'down' : probe.loss >= 0.4 ? 'critical' : probe.loss > 0 ? 'degraded' : 'healthy';
  const stale = candidate.stale ?? false;
  const ageSeconds = stale ? candidate.ageSeconds ?? 730 : 18;
  const rttMetrics = [
    ...(probe.min === undefined ? [] : [{ name: RTT_METRIC, value: probe.min, labels: { stat: 'min' } }]),
    ...(probe.avg === undefined ? [] : [{ name: RTT_METRIC, value: probe.avg, labels: { stat: 'avg' } }]),
    ...(probe.max === undefined ? [] : [{ name: RTT_METRIC, value: probe.max, labels: { stat: 'max' } }]),
    ...(probe.deviation === undefined ? [] : [{ name: RTT_METRIC, value: probe.deviation, labels: { stat: 'mdev' } }])
  ];
  const metrics = [{ name: LOSS_METRIC, value: probe.loss, labels: {} }, ...rttMetrics.sort((left, right) => left.labels.stat.localeCompare(right.labels.stat))];
  return { sourceDeviceId: candidate.sourceDeviceId, sourceIp: candidate.sourceIp, destinationIp: candidate.destinationIp, latest: { observedAt: new Date(generatedAt.getTime() - ageSeconds * 1000).toISOString(), status, freshness: { state: stale ? 'stale' : 'fresh', ageSeconds }, metrics } };
}

function rangeCandidate(candidate: CandidateFixture, history: ProbeFixture[]) {
  const rtts = packetRTTs(history);
  const sent = history.reduce((total, probe) => total + probe.packetsSent, 0);
  const received = history.reduce((total, probe) => total + probe.packetsReceived, 0);
  const loss = sent === 0 ? 0 : 1 - received / sent;
  const availability = history.length === 0 ? 0 : 1 - history.filter((probe) => probe.executionFailed).length / history.length;
  const histogramBuckets = DISPLAY_HISTOGRAM_BUCKETS.map((bucket) => {
    const count = rtts.filter((value) => {
      const milliseconds = value * 1000;
      const isAboveLowerBound = bucket.lowerMs === 0 ? milliseconds >= 0 : milliseconds > bucket.lowerMs;
      return isAboveLowerBound && (bucket.upperMs === null || milliseconds <= bucket.upperMs);
    }).length;
    return { ...bucket, count, percentage: rtts.length === 0 ? 0 : count / rtts.length * 100 };
  });
  return { sourceDeviceId: candidate.sourceDeviceId, sourceIp: candidate.sourceIp, destinationIp: candidate.destinationIp, history: {
    series: rttSeries(history, rtts),
    values: [
      ...(rtts.length === 0 ? [] : [{ name: RTT_METRIC, value: rtts.reduce((sum, value) => sum + value, 0) / rtts.length, labels: { stat: 'mean' } }]),
      ...(['p50', 'p90', 'p95', 'p99'] as const).flatMap((stat) => rtts.length === 0 ? [] : [{ name: RTT_METRIC, value: percentile(rtts, Number(stat.slice(1)) / 100), labels: { stat } }]),
      { name: LOSS_METRIC, value: loss, labels: {} },
      { name: 'ping_mesh_availability_ratio', value: availability, labels: {} }
    ],
    ranges: rtts.length === 0 ? [] : [{ name: RTT_METRIC, lower: percentile(rtts, 0.5), upper: percentile(rtts, 0.9), labels: { stat: 'baseline' } }],
    distributions: rtts.length === 0 ? [] : [{ name: 'ping_mesh_packet_rtt_seconds', count: rtts.length, sumSeconds: rtts.reduce((sum, value) => sum + value, 0), buckets: histogramBuckets }]
  } };
}

function response(body: unknown): Response {
  return new Response(JSON.stringify(body), { status: 200, headers: { 'Content-Type': 'application/json', 'X-Mock-Route': 'metrics' } });
}

function matchPingMeshPath(url: URL): boolean {
  const parts = url.pathname.split('/').filter(Boolean);
  const orgsIndex = parts.indexOf('orgs');
  return orgsIndex >= 0 && parts.slice(orgsIndex + 2).join('/') === 'metrics/ping-mesh';
}

export const metricsHandlers: MockHandler[] = [
  async ({ request, url }) => {
    if (!matchPingMeshPath(url) || request.method !== 'POST') return null;
    const body = await request.clone().json().catch(() => null) as { selector?: { sourceSiteId?: string; destinationSiteId?: string }; query?: { mode?: string; lookback?: string } } | null;
    if (body?.query?.mode === 'latest') {
      const generatedAt = new Date();
      return response({ generatedAt: generatedAt.toISOString(), cells: CELLS.map((cell) => ({ sourceSiteId: cell.sourceSiteId, destinationSiteId: cell.destinationSiteId, candidates: cell.candidates.map((candidate) => latestCandidate(candidate, generatedAt)) })) });
    }
    if (body?.query?.mode !== 'range' || !body.selector?.sourceSiteId || !body.selector.destinationSiteId || !body.query.lookback) return new Response(JSON.stringify({ detail: 'A valid Ping Mesh query is required.' }), { status: 400, headers: { 'Content-Type': 'application/json', 'X-Mock-Route': 'metrics' } });
    const generatedAt = new Date();
    const lookback = duration(body.query.lookback);
    const cell = CELLS.find((entry) => entry.sourceSiteId === body.selector?.sourceSiteId && entry.destinationSiteId === body.selector?.destinationSiteId);
    if (!lookback || !cell) return response({ generatedAt: generatedAt.toISOString(), range: { start: new Date(generatedAt.getTime() - (lookback ?? 0)).toISOString(), end: generatedAt.toISOString(), lookback: body.query.lookback }, cells: [] });
    const earliest = generatedAt.getTime() - lookback;
    return response({ generatedAt: generatedAt.toISOString(), range: { start: new Date(earliest).toISOString(), end: generatedAt.toISOString(), lookback: body.query.lookback }, cells: [{ sourceSiteId: cell.sourceSiteId, destinationSiteId: cell.destinationSiteId, candidates: cell.candidates.map((candidate) => rangeCandidate(candidate, historyForRange(candidate.history, lookback, generatedAt))) }] });
  }
];
